import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils";
import { getIcon, getColorClass } from "@/utils/icon-utils";
import type { IconColor } from "@/types/icon";

const badgeVariants = cva(
  "inline-flex items-center transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2",
  {
    variants: {
      variant: {
        default: "bg-muted text-muted-foreground rounded-full px-3 py-1",
        primary: "bg-primary text-primary-foreground rounded-full px-3 py-1",
        secondary: "bg-secondary text-secondary-foreground rounded-full px-3 py-1",
        outline: "border border-border bg-background text-foreground rounded-full px-3 py-1",
        destructive: "bg-destructive text-destructive-foreground rounded-full px-3 py-1",
        ghost: "bg-transparent text-foreground hover:bg-accent hover:text-accent-foreground rounded-md px-2 py-1",
      },
      size: {
        sm: "text-xs gap-1.5",
        md: "text-sm gap-2",
        lg: "text-base gap-2.5",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "md",
    },
  }
);

export interface BadgeProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof badgeVariants> {
  /**
   * Optional icon name (must match a Lucide icon name)
   * @example "Building2", "Sparkles", "TrendingUp"
   */
  icon?: string;
  
  /**
   * Color of the icon - can be a semantic name or Tailwind class
   * @example "blue", "purple", "success", "text-green-500"
   * @default "default"
   */
  iconColor?: IconColor;
}

const iconSizeClasses = {
  sm: "h-3 w-3",
  md: "h-4 w-4",
  lg: "h-5 w-5",
} as const;

/**
 * Badge Component
 * 
 * A flexible badge component that displays content with an optional icon.
 * Supports multiple sizes, variants, and custom colors.
 * 
 * @example
 * ```tsx
 * // Basic usage
 * <Badge>Fortune 500 Experience</Badge>
 * 
 * // With icon and color
 * <Badge icon="Sparkles" iconColor="purple">
 *   AI-native Platform
 * </Badge>
 * 
 * // Different variant and size
 * <Badge 
 *   icon="TrendingUp" 
 *   iconColor="green"
 *   variant="secondary"
 *   size="lg"
 * >
 *   100% Performance-Based
 * </Badge>
 * ```
 */
export function Badge({ 
  className, 
  variant, 
  size,
  icon,
  iconColor,
  children,
  ...props 
}: BadgeProps) {
  const Icon = icon ? getIcon(icon) : null;
  const iconColorClass = getColorClass(iconColor);
  
  return (
    <div 
      className={cn(badgeVariants({ variant, size }), className)} 
      {...props}
    >
      {Icon && (
        <Icon 
          className={cn(
            iconSizeClasses[size || "md"],
            iconColorClass
          )} 
          aria-hidden="true"
        />
      )}
      {children}
    </div>
  );
}

/**
 * Badge Group Component
 * 
 * Renders a group of badges with consistent spacing
 * 
 * @example
 * ```tsx
 * <BadgeGroup>
 *   <Badge icon="Star" iconColor="yellow">Feature 1</Badge>
 *   <Badge icon="Shield" iconColor="blue">Feature 2</Badge>
 *   <Badge icon="Zap" iconColor="purple">Feature 3</Badge>
 * </BadgeGroup>
 * ```
 */
export function BadgeGroup({ 
  children, 
  className 
}: { 
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={cn(
      "flex flex-wrap items-center gap-4",
      className
    )}>
      {children}
    </div>
  );
}